/**
 * Real User Monitoring — captures Web Vitals + SPA navigation timings,
 * batches them, and sends to /perf/ingest every 10s or on unload.
 */
const API_URL = (import.meta.env.VITE_API_URL as string) || '/api';
const BATCH_INTERVAL_MS = 10_000;
const MAX_BATCH = 20;

interface Sample { metric: string; value_ms: number }
let queue: Sample[] = [];
let currentRoute = typeof window !== 'undefined' ? window.location.pathname : '/';

function flush() {
  if (queue.length === 0) return;
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  // Anonymous samples are SENT, not dropped. The ingest endpoint mounts
  // `optionalAuth` precisely so it can accept them ("we want performance data
  // from the whole audience"), but this function used to bail out and clear
  // the queue whenever there was no token — silently discarding every
  // measurement from logged-out visitors, which is most of the marketing
  // site. sendBeacon cannot carry an Authorization header anyway, so the
  // authenticated path already relied on the cookie-less endpoint working.
  const payload = JSON.stringify({
    platform: 'web',
    route: currentRoute,
    samples: queue.splice(0, MAX_BATCH),
  });
  try {
    const url = `${API_URL}/perf/ingest`;
    // Beacon survives page unload; only usable when we have no header to add.
    if (!token && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // swallow — RUM never breaks UX
  }
}

function record(metric: string, value_ms: number) {
  if (!Number.isFinite(value_ms) || value_ms < 0 || value_ms > 300_000) return;
  queue.push({ metric, value_ms: Math.round(value_ms * 100) / 100 });
  if (queue.length >= MAX_BATCH) flush();
}

export function initRUM() {
  if (typeof window === 'undefined' || (window as any).__rumInit) return;
  (window as any).__rumInit = true;

  // PerformanceObserver-based web-vitals without pulling a dep.
  try {
    // LCP
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      const v = (last.renderTime || last.loadTime || (last as any).startTime) as number;
      if (v) record('LCP', v);
    }).observe({ type: 'largest-contentful-paint', buffered: true } as any);

    // CLS (cumulative) — flush on visibility change
    let clsValue = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) clsValue += entry.value;
      }
    }).observe({ type: 'layout-shift', buffered: true } as any);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && clsValue > 0) {
        record('CLS', clsValue * 1000); // store as ms-scale for easy comparison
        clsValue = 0;
        flush();
      }
    });

    // INP (interaction latency)
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (entry.duration > 0) record('INP', entry.duration);
      }
    }).observe({ type: 'event', buffered: true, durationThreshold: 40 } as any);

    // TTFB (from navigation timing)
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) record('TTFB', nav.responseStart - nav.requestStart);
  } catch {
    // quiet: browser doesn't support PerformanceObserver
  }

  // SPA route-change timing
  let lastRoute = window.location.pathname;
  let lastChange = performance.now();
  const onNav = () => {
    const now = performance.now();
    const duration = now - lastChange;
    if (duration > 0 && duration < 30_000 && window.location.pathname !== lastRoute) {
      record('nav', duration);
      lastRoute = window.location.pathname;
      currentRoute = lastRoute;
    }
    lastChange = now;
  };
  window.addEventListener('popstate', onNav);
  // Wrap pushState/replaceState to catch client nav
  const _push = history.pushState;
  const _replace = history.replaceState;
  history.pushState = function (...args) { _push.apply(this, args); onNav(); };
  history.replaceState = function (...args) { _replace.apply(this, args); onNav(); };

  // Error RATE, deliberately without message or stack.
  //
  // Before this there was no ungated error signal at all: PostHog's exception
  // capture sits behind cookie consent, so an anonymous visitor hitting a
  // broken page produced nothing anywhere. Counting errors per route closes
  // that -- a spike on /pricing is the alarm; PostHog and Workers Logs carry
  // the detail for whoever investigates.
  //
  // No message, no stack, no filename: exception text routinely contains user
  // input and tokens, and this endpoint is first-party but ungated, so the
  // only safe payload is a count. Recorded as value 0 because the samples
  // table stores durations -- the row's existence is the signal.
  window.addEventListener('error', (e) => {
    // Resource load failures (img/script 404s) also fire here with no `error`
    // object; they are not JS exceptions and would drown the real signal.
    if (!(e as ErrorEvent).error) return;
    record('js_error', 0);
  });
  window.addEventListener('unhandledrejection', () => record('unhandled_rejection', 0));

  setInterval(flush, BATCH_INTERVAL_MS);
  window.addEventListener('beforeunload', flush);
}
